import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Alert,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SaveIcon from '@mui/icons-material/Save';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import { format, addDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import agent from '../api/axios';
import { useLocation } from 'react-router-dom';
import PayeeSelector from '../components/PayeeSelector';
import WeekSelector from '../components/WeekSelector';
import TimesheetTable from '../components/TimesheetTable';
import TimesheetTotals from '../components/TimesheetTotals';
import { PageHeader } from '../components/ui';
import { useKeyboardShortcut } from '../hooks';
import { usePayees, useSubmitTimesheet } from '../hooks/useApi';

import { TimesheetRowType } from '../types/timesheet';

interface TimesheetEntry {
  task: string;
  notes: string;
  hours: number[];
}

interface Timesheet {
  payeeId: string;
  weekEnding: string;
  payCheckDate?: string;
  entries: TimesheetEntry[];
  totalHours: number;
}

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function TimesheetForm() {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const initialPayeeId = location.state?.payeeId || '';
  const { data: payees = [] } = usePayees();
  const submitMutation = useSubmitTimesheet();
  const [payeeId, setPayeeId] = useState(initialPayeeId);
  const [endOfWeekDay, setEndOfWeekDay] = useState(0);
  const [weekEnding, setWeekEnding] = useState(() => getNextWeekEnding(0));
  const [existingTimesheet, setExistingTimesheet] = useState<Timesheet | null>(
    null
  );
  const [flatRateAmount, setFlatRateAmount] = useState<number>(0);
  const saving = submitMutation.isPending;

  // Get the selected payee's payment type
  const selectedPayee = payees.find((p: any) => p.id === payeeId);
  const paymentType = selectedPayee?.paymentType || 'hourly';
  const isFlatRate = paymentType === 'flatRate';

  // Keyboard shortcuts
  useKeyboardShortcut(
    { key: 's', ctrlKey: true },
    () => {
      if (payeeId) {
        handleSubmit();
      }
    },
    !saving
  );

  useKeyboardShortcut(
    { key: 'n', ctrlKey: true },
    () => {
      addRow();
    },
    true
  );

  // Update weekEnding when endOfWeekDay changes
  useEffect(() => {
    setWeekEnding(getNextWeekEnding(endOfWeekDay));
  }, [endOfWeekDay]);

  // when endOfWeekDay changes, compute the target weekEnding and fetch that timesheet
  useEffect(() => {
    if (!payeeId) return;

    const targetWeekEnding = getNextWeekEnding(endOfWeekDay);

    // if state already matches the computed value, fetch immediately
    if (targetWeekEnding === weekEnding) {
      agent.Timesheets.getByWeekEnding(payeeId, targetWeekEnding)
        .then((result: Timesheet) => {
          setExistingTimesheet(result);
        })
        .catch(() => {
          setExistingTimesheet(null);
        });
      return;
    }

    // otherwise update weekEnding state — the other effect will fetch
    setWeekEnding(targetWeekEnding);
    // Note: weekEnding is intentionally excluded - we're computing a new target here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payeeId, endOfWeekDay]); // runs when payee or endOfWeekDay change

  // fetch when weekEnding changes (user selection or updated by endOfWeekDay effect)
  useEffect(() => {
    if (!payeeId || !weekEnding) return;

    agent.Timesheets.getByWeekEnding(payeeId, weekEnding)
      .then((result: Timesheet) => {
        setExistingTimesheet(result);
      })
      .catch(() => {
        setExistingTimesheet(null);
      });
  }, [payeeId, weekEnding]);

  function getNextWeekEnding(endOfWeekDay: number, fromDate = new Date()) {
    const currentDay = fromDate.getDay();
    const diff = (endOfWeekDay - currentDay + 7) % 7 || 7;
    const nextDate = new Date(fromDate);
    nextDate.setDate(fromDate.getDate() + diff);
    return format(nextDate, 'yyyy-MM-dd');
  }

  const navigateToPreviousWeek = () => {
    const current = new Date(weekEnding);
    current.setDate(current.getDate() - 7);
    setWeekEnding(format(current, 'yyyy-MM-dd'));
  };

  const navigateToNextWeek = () => {
    const current = new Date(weekEnding);
    current.setDate(current.getDate() + 7);
    setWeekEnding(format(current, 'yyyy-MM-dd'));
  };

  const weekDays = Array(7)
    .fill(0)
    .map((_, i) => format(addDays(new Date(weekEnding), i), 'EEE'));

  const [rows, setRows] = useState<TimesheetRowType[]>([
    { task: '', notes: '', hours: Array(weekDays.length).fill(0) },
  ]);

  // when an existing timesheet arrives (or weekDays changes) initialize rows from it
  useEffect(() => {
    const numDays = weekDays.length;
    if (!existingTimesheet) {
      setRows([{ task: '', notes: '', hours: Array(numDays).fill(0) }]);
      return;
    }

    const mapped = existingTimesheet.entries.map((e) => {
      const hours = Array.from(e.hours || []);
      // ensure hours array length matches weekDays
      if (hours.length < numDays) {
        hours.push(...Array(numDays - hours.length).fill(0));
      } else if (hours.length > numDays) {
        hours.length = numDays;
      }
      return { task: e.task, notes: e.notes, hours } as TimesheetRowType;
    });

    setRows(
      mapped.length
        ? mapped
        : [{ task: '', notes: '', hours: Array(numDays).fill(0) }]
    );
  }, [existingTimesheet, weekDays.length]);

  // Set endOfWeekDay when payees are loaded
  useEffect(() => {
    if (payees && payees.length && typeof payees[0]?.endOfWeek === 'number') {
      setEndOfWeekDay(payees[0].endOfWeek);
    }
  }, [payees]);

  // Handlers (immutable, accept empty input for numbers)
  const updateRowField = (
    rowIndex: number,
    field: keyof TimesheetRowType,
    value: string | number | number[]
  ) => {
    setRows((old) =>
      old.map((r, i) => (i === rowIndex ? { ...r, [field]: value } : r))
    );
  };

  const updateHours = (
    rowIndex: number,
    dayIndex: number,
    value: string | number
  ) => {
    // allow empty string while typing, coerce to number for storage/calculation
    const parsed = value === '' ? '' : Number(value);
    setRows((old) =>
      old.map((r, i) => {
        if (i !== rowIndex) return r;
        const newHours = [...r.hours];
        newHours[dayIndex] =
          parsed === '' || Number.isNaN(parsed) ? 0 : (parsed as number);
        return { ...r, hours: newHours };
      })
    );
  };

  const addRow = () => {
    setRows((old) => [
      ...old,
      { task: '', notes: '', hours: Array(weekDays.length).fill(0) },
    ]);
  };

  const removeRow = (rowIndex: number) => {
    if (rows.length === 1) return; // Keep at least one row
    setRows((old) => old.filter((_, i) => i !== rowIndex));
  };

  // Totals
  const totalHoursPerDay = (dayIndex: number) =>
    rows.reduce((sum, row) => sum + (row.hours[dayIndex] || 0), 0);

  const totalHours = rows.reduce(
    (sum, row) => sum + row.hours.reduce((rSum, h) => rSum + h, 0),
    0
  );

  // Submit
  const handleSubmit = async () => {
    if (!payeeId) {
      enqueueSnackbar('Please select a payee', { variant: 'error' });
      return;
    }

    if (isFlatRate && flatRateAmount <= 0) {
      enqueueSnackbar('Please enter a payment amount', { variant: 'error' });
      return;
    }

    const payload = {
      payeeId,
      weekEnding,
      entries: rows.map((row) => ({
        task: row.task,
        notes: row.notes,
        hours: row.hours,
      })),
      totalHours,
      paymentType: isFlatRate ? ('flatRate' as const) : ('hourly' as const),
      flatRateAmount: isFlatRate ? flatRateAmount : 0,
    };

    submitMutation.mutate(payload);
  };

  // Dates for tooltips
  const weekDates = Array(7)
    .fill(0)
    .map((_, i) => format(addDays(new Date(weekEnding), i), 'MMM d'));

  return (
    <Box className="fade-in" sx={{ maxWidth: 1200, margin: 'auto' }}>
      <PageHeader
        title="Weekly Timesheet"
        subtitle="Track hours for your contractors"
        icon={<ScheduleIcon />}
        iconColor="warning"
      />

      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
            alignItems="flex-start"
          >
            <Box sx={{ minWidth: 360 }}>
              <PayeeSelector
                payees={payees}
                payeeId={payeeId}
                setPayeeId={setPayeeId}
              />
            </Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={navigateToPreviousWeek}
                sx={{ minWidth: 40, px: 1 }}
              >
                <ArrowBackIcon />
              </Button>
              <Box sx={{ flex: 1 }}>
                <WeekSelector
                  weekEnding={weekEnding}
                  setWeekEnding={setWeekEnding}
                  endOfWeekDay={endOfWeekDay}
                />
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={navigateToNextWeek}
                sx={{ minWidth: 40, px: 1 }}
              >
                <ArrowForwardIcon />
              </Button>
            </Box>
          </Box>

          {/* Flat Rate Payment Input */}
          {isFlatRate && payeeId && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: (theme) => theme.palette.custom.primaryTint,
                borderRadius: 2,
                border: '1px solid',
                borderColor: (theme) => theme.palette.custom.primaryTintBorder,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
              >
                <AttachMoneyIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Flat Rate Payment
                </Typography>
                <Chip
                  label="Flat Rate Payee"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the total payment amount for this week. Hours are tracked
                for records only.
              </Typography>
              <TextField
                label="Payment Amount"
                type="number"
                value={flatRateAmount || ''}
                onChange={(e) =>
                  setFlatRateAmount(parseFloat(e.target.value) || 0)
                }
                size="small"
                sx={{ width: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2 }}>
          {!isFlatRate && (
            <>
              <TimesheetTable
                rows={rows}
                updateRowField={updateRowField}
                updateHours={updateHours}
                removeRow={removeRow}
                weekDates={weekDates}
                weekDays={weekDays}
              />

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={addRow}
                  startIcon={<AddIcon />}
                  sx={{ textTransform: 'none' }}
                  aria-label="Add new task row (Ctrl+N)"
                >
                  Add Task Row
                </Button>
              </Box>

              <TimesheetTotals
                totalHoursPerDay={totalHoursPerDay}
                totalHours={totalHours}
                daysOfWeek={daysOfWeek}
              />
            </>
          )}
        </Box>

        <Box
          sx={{
            p: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) => theme.palette.custom.softBackground,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={
              saving ||
              !payeeId ||
              (isFlatRate ? flatRateAmount <= 0 : totalHours === 0)
            }
            startIcon={<SaveIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
            }}
            aria-label="Submit timesheet (Ctrl+S)"
          >
            {saving ? 'Saving...' : 'Submit Timesheet'}
          </Button>
        </Box>
      </Card>

      {/* Keyboard Shortcuts Help */}
      <Alert severity="info" sx={{ mt: 2 }} icon={false}>
        <Typography variant="body2">
          <strong>Keyboard Shortcuts:</strong> Ctrl+S to save • Ctrl+N to add
          new row
        </Typography>
      </Alert>
    </Box>
  );
}
